import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HOTSPOT_LABELS } from "../../lib/lumenDock";
import {
  IconLink, IconMore, IconPencil, IconPlus, IconSidebar, IconSparkle, IconTrash, SURFACE_ICONS,
} from "../../lib/Icons/SessionIcons";

const TEAL = "#4DB8B5";
const TEAL_DARK = "#2F8C89";
const FONT = "'DM Sans', sans-serif";
export const NEW_SESSION_TITLE = "New session";

export function formatSessionTime(iso) {
  const date = iso ? new Date(iso) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function sessionTitle(session) {
  return session?.title || NEW_SESSION_TITLE;
}

export function sessionListItems(sessions) {
  const items = [...(sessions?.items || [])].sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)));
  if (sessions?.mode === "building") return items;
  return sessions?.activeId ? items : [{ id: null, title: null, surface: null, updated_at: null, draft: true }, ...items];
}

export function buildingGroups(sessions) {
  const items = sessionListItems(sessions);
  return (sessions?.rooms || [])
    .map((room) => ({ ...room, items: items.filter((item) => item.room_id === room.id) }))
    .filter((group) => group.items.length > 0);
}

export function SessionTag({ surface }) {
  const Icon = SURFACE_ICONS[surface];
  const label = surface ? HOTSPOT_LABELS[surface] || null : null;
  return (
    <span
      data-session-tag={surface || "lumen"}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4, padding: label ? "2px 7px 2px 5px" : "2px 5px",
        borderRadius: 6, background: "rgba(77,184,181,0.12)", border: "1px solid rgba(77,184,181,0.25)",
        color: TEAL_DARK, fontSize: 11, fontWeight: 600, fontFamily: FONT, lineHeight: 1.3, whiteSpace: "nowrap",
      }}
    >
      {Icon && label ? <Icon size={12} /> : <IconSparkle size={12} />}
      {Icon && label && <span>{label}</span>}
    </span>
  );
}

const iconButton = {
  background: "none", border: "none", color: "#475569", cursor: "pointer",
  padding: 5, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8,
};

const MENU_WIDTH = 140;
const MENU_HEIGHT = 118;

const menuItem = {
  display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "7px 10px", border: "none",
  background: "none", cursor: "pointer", fontSize: 12.5, fontFamily: FONT, color: "#334155", textAlign: "left", borderRadius: 6,
};

function SessionRow({ session, active, onOpen, onRename, onDelete, onLink, showMenu = true }) {
  const [hover, setHover] = useState(false);
  const [menuAt, setMenuAt] = useState(null);
  const menuOpen = !!menuAt;
  const menuRef = useRef(null);
  const [mode, setMode] = useState("view");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const rowRef = useRef(null);
  const committingRef = useRef(false);
  const isDraft = !!session.draft;
  const renaming = mode === "rename";
  const Body = renaming ? "div" : "button";

  useEffect(() => {
    if (mode === "rename") inputRef.current?.select();
  }, [mode]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (!rowRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) setMenuAt(null);
    };
    const dismiss = () => setMenuAt(null);
    document.addEventListener("mousedown", close);
    window.addEventListener("resize", dismiss);
    document.addEventListener("scroll", dismiss, true);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("resize", dismiss);
      document.removeEventListener("scroll", dismiss, true);
    };
  }, [menuOpen]);

  const toggleMenu = (e) => {
    if (menuOpen) {
      setMenuAt(null);
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    const below = r.bottom + 4 + MENU_HEIGHT <= window.innerHeight;
    setMenuAt({
      left: Math.max(8, r.right - MENU_WIDTH),
      top: below ? r.bottom + 4 : Math.max(8, r.top - 4 - MENU_HEIGHT),
    });
  };

  const startRename = () => {
    setMenuAt(null);
    setDraft(sessionTitle(session));
    setMode("rename");
  };

  const commitRename = async () => {
    if (committingRef.current) return;
    const next = draft.trim();
    if (!next || next === sessionTitle(session)) {
      setMode("view");
      return;
    }
    committingRef.current = true;
    setBusy(true);
    await onRename(session.id, next);
    committingRef.current = false;
    setBusy(false);
    setMode("view");
  };

  const confirmDelete = async () => {
    setBusy(true);
    await onDelete(session.id);
    setBusy(false);
    setMode("view");
  };

  if (mode === "confirm-delete") {
    return (
      <div data-session-row="confirm" style={{ padding: "10px 11px", borderRadius: 10, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)" }}>
        <div style={{ fontSize: 12.5, color: "#334155", fontFamily: FONT, marginBottom: 8 }}>
          Delete “{sessionTitle(session)}”? This can’t be undone.
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" disabled={busy} onClick={confirmDelete}
            style={{ flex: 1, padding: "6px 8px", borderRadius: 8, border: "none", background: "#EF4444", color: "white", fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: busy ? "progress" : "pointer" }}>
            Delete
          </button>
          <button type="button" disabled={busy} onClick={() => setMode("view")}
            style={{ flex: 1, padding: "6px 8px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", background: "white", color: "#334155", fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rowRef}
      data-session-row={active ? "active" : "idle"}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ position: "relative" }}
    >
      <Body
        {...(renaming ? {} : { type: "button", onClick: () => { if (!active) onOpen(session.id); }, title: sessionTitle(session) })}
        style={{
          display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, width: "100%", textAlign: "left",
          boxSizing: "border-box", padding: "9px 30px 9px 11px", borderRadius: 10, border: "none",
          cursor: active || renaming ? "default" : "pointer",
          background: active ? "rgba(77,184,181,0.16)" : hover ? "rgba(0,0,0,0.035)" : "transparent",
          fontFamily: FONT, transition: "background 0.15s",
        }}
      >
        {renaming ? (
          <input
            ref={inputRef}
            value={draft}
            disabled={busy}
            maxLength={80}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commitRename(); }
              if (e.key === "Escape") { e.preventDefault(); setMode("view"); }
            }}
            onBlur={commitRename}
            aria-label="Session name"
            style={{
              width: "100%", boxSizing: "border-box", fontSize: 13, fontWeight: 600, fontFamily: FONT, color: "#1E293B",
              border: `1px solid ${TEAL}`, borderRadius: 6, padding: "3px 6px", outline: "none", background: "white",
            }}
          />
        ) : (
          <span style={{
            fontSize: 13, fontWeight: 600, lineHeight: 1.35, color: active ? TEAL_DARK : "#1E293B",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word",
          }}>
            {sessionTitle(session)}
          </span>
        )}
        <span style={{ fontSize: 11.5, color: active ? TEAL_DARK : "#64748B" }}>{formatSessionTime(session.updated_at)}</span>
        {!isDraft && <SessionTag surface={session.surface} />}
      </Body>

      {showMenu && !isDraft && mode === "view" && (hover || menuOpen) && (
        <button
          type="button"
          aria-label="Session options"
          onClick={toggleMenu}
          style={{ ...iconButton, position: "absolute", top: 6, right: 4, padding: 4, background: menuOpen ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.8)" }}
        >
          <IconMore size={15} />
        </button>
      )}

      {menuOpen && createPortal(
        <div ref={menuRef} role="menu" style={{
          position: "fixed", top: menuAt.top, left: menuAt.left, zIndex: 1000, width: MENU_WIDTH, boxSizing: "border-box",
          padding: 4, borderRadius: 10, background: "white", border: "1px solid rgba(0,0,0,0.08)", boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
        }}>
          <button type="button" role="menuitem" style={menuItem} onClick={startRename}>
            <IconPencil size={14} /> Rename
          </button>
          {onLink && (
            <button type="button" role="menuitem" style={menuItem} onClick={() => { setMenuAt(null); onLink(session.id); }}>
              <IconLink size={14} /> Related chats
            </button>
          )}
          <button type="button" role="menuitem" style={{ ...menuItem, color: "#DC2626" }}
            onClick={() => { setMenuAt(null); setMode("confirm-delete"); }}>
            <IconTrash size={14} /> Delete
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

function RelatedPicker({ session, candidates, selected, onDone }) {
  const [picked, setPicked] = useState(() => new Set(selected));
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const save = async () => {
    setBusy(true);
    await onDone([...picked]);
    setBusy(false);
  };
  return (
    <div data-lumen-related-picker style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 0, flex: 1 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1E293B", fontFamily: FONT }}>
        Related chats for “{sessionTitle(session)}”
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, overflowY: "auto", minHeight: 0 }}>
        {candidates.length === 0 && (
          <div style={{ fontSize: 12, color: "#64748B", fontFamily: FONT }}>No other chats in this room yet.</div>
        )}
        {candidates.map((item) => (
          <label key={item.id} style={{
            display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", borderRadius: 8, cursor: "pointer",
            background: picked.has(item.id) ? "rgba(77,184,181,0.12)" : "transparent", fontSize: 12.5, fontFamily: FONT, color: "#1E293B",
          }}>
            <input type="checkbox" checked={picked.has(item.id)} onChange={() => toggle(item.id)} style={{ accentColor: TEAL }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sessionTitle(item)}</span>
          </label>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button type="button" disabled={busy} onClick={save}
          style={{ flex: 1, padding: "7px 8px", borderRadius: 8, border: "none", background: TEAL, color: "white", fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: busy ? "progress" : "pointer" }}>
          Save
        </button>
        <button type="button" disabled={busy} onClick={() => onDone(null)}
          style={{ flex: 1, padding: "7px 8px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", background: "white", color: "#334155", fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: "pointer" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function SessionSidebar({ identity, sessions, onHide }) {
  const [linkingId, setLinkingId] = useState(null);
  const building = sessions.mode === "building";
  const items = sessionListItems(sessions);
  const linking = linkingId ? items.find((item) => item.id === linkingId) : null;

  const row = (session) => (
    <SessionRow
      key={session.id || "draft"}
      session={session}
      active={session.draft ? !sessions.activeId : session.id === sessions.activeId}
      onOpen={sessions.onOpen}
      onRename={sessions.onRename}
      onDelete={sessions.onDelete}
      onLink={!building && sessions.onSetRelated ? setLinkingId : null}
      showMenu={!building}
    />
  );

  return (
    <div data-lumen-sessions="list" style={{ display: "flex", flexDirection: "column", gap: 10, minHeight: 0, flex: 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>{identity}</div>
        <button type="button" onClick={onHide} title="Hide sessions" aria-label="Hide sessions" style={iconButton}>
          <IconSidebar size={17} />
        </button>
      </div>
      {linking ? (
        <RelatedPicker
          session={linking}
          candidates={items.filter((item) => !item.draft && item.id !== linking.id)}
          selected={sessions.relatedOf ? sessions.relatedOf(linking.id) : []}
          onDone={async (ids) => {
            if (ids) await sessions.onSetRelated(linking.id, ids);
            setLinkingId(null);
          }}
        />
      ) : (
        <>
          {!building && (
            <button
              type="button"
              onClick={sessions.onNew}
              disabled={!sessions.activeId}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "7px 10px", borderRadius: 10,
                border: "1px solid rgba(77,184,181,0.45)", background: "white", color: TEAL_DARK, fontSize: 12.5, fontWeight: 600,
                fontFamily: FONT, cursor: sessions.activeId ? "pointer" : "default", opacity: sessions.activeId ? 1 : 0.55,
              }}
            >
              <IconPlus size={14} /> New session
            </button>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 2, overflowY: "auto", minHeight: 0, margin: "0 -4px", padding: "0 4px" }}>
            {building ? (
              buildingGroups(sessions).length === 0 ? (
                <div style={{ fontSize: 12, color: "#64748B", fontFamily: FONT, padding: "4px 7px" }}>No chats in this building yet.</div>
              ) : buildingGroups(sessions).map((group) => (
                <div key={group.id} data-session-group={group.id} style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#94A3B8", fontFamily: FONT, padding: "4px 7px 2px" }}>
                    {group.name}
                  </div>
                  {group.items.map(row)}
                </div>
              ))
            ) : items.map(row)}
          </div>
        </>
      )}
    </div>
  );
}

const RAIL_DOTS = 8;

export function SessionRail({ sessions, onShow }) {
  const items = sessionListItems(sessions);
  return (
    <div data-lumen-sessions="rail" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <button type="button" onClick={onShow} title="Show sessions" aria-label="Show sessions" style={iconButton}>
        <IconSidebar size={18} />
      </button>
      {sessions.mode !== "building" && <button
        type="button"
        onClick={sessions.onNew}
        disabled={!sessions.activeId}
        title="Start new session"
        aria-label="Start new session"
        style={{
          ...iconButton, width: 34, height: 34, padding: 0, border: "1px solid rgba(0,0,0,0.12)", background: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)", opacity: sessions.activeId ? 1 : 0.55,
          cursor: sessions.activeId ? "pointer" : "default",
        }}
      >
        <IconPlus size={16} />
      </button>}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        {items.slice(0, RAIL_DOTS).map((session) => {
          const active = session.draft ? !sessions.activeId : session.id === sessions.activeId;
          return (
            <button
              key={session.id || "draft"}
              type="button"
              title={sessionTitle(session)}
              aria-label={sessionTitle(session)}
              onClick={() => { if (!active && !session.draft) sessions.onOpen(session.id); }}
              style={{ border: "none", background: "none", padding: 5, cursor: active ? "default" : "pointer", display: "flex" }}
            >
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: active ? TEAL_DARK : "#CBD5E1" }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
